/**
 * Stamp ``X-Dev-Caller-Email`` on outgoing requests when running locally.
 *
 * The Beacon ``caller_email()`` helper (in ``pam.servers.utils.auth``)
 * recognizes this header as a *dev-only* identity signal — it is honored
 * **only** when the server has set ``PAM_ALLOW_DEV_HEADER=1`` AND no
 * ``x-ms-client-principal`` is present on the request. This lets
 * ``npm start`` workflows identify the caller without leaking the email
 * into the URL bar (which prior ``?caller_email=`` / ``?email=``
 * fallbacks did, making impersonation a one-character edit and tainting
 * every server log line with the caller's address).
 *
 * Defense in depth — three independent gates keep the header inert in
 * production:
 *   1. The interceptor only fires when ``process.env.NODE_ENV !==
 *      'production'`` (this file). CRA's DefinePlugin substitutes that
 *      literal at build time, so a prod bundle has the gate compiled to
 *      ``'production' !== 'production'`` (always false) and the header
 *      stamping path is dead-code-eliminated. Covers ``npm start`` on
 *      ``localhost`` AND ``npm start`` exposed through an ngrok tunnel
 *      (where the page hostname is the ngrok subdomain, not localhost) —
 *      the prior ``hostname === 'localhost'`` gate refused to fire on the
 *      tunnel and left the request unauthenticated.
 *   2. ``REACT_APP_DEV_USER`` is unset in any prod build.
 *   3. The Beacon server requires ``PAM_ALLOW_DEV_HEADER=1`` to honor
 *      the header at all, and the SWA Function proxy strips arbitrary
 *      inbound headers before forwarding to Beacon — so even if the
 *      bundle somehow set the header in prod, the proxy drops it.
 *
 * Idempotent: safe to call multiple times on the same axios instance
 * (e.g., when several apps construct several ``createAppTransport``
 * clients on the shared default axios singleton). A flag on the axios
 * instance gates re-registration so headers don't end up applied twice.
 *
 * @param {Object} axios - Axios instance (or default singleton).
 */
const FLAG = '__1823_dev_caller_email_interceptor__';

export const attachDevCallerEmailInterceptor = (axios) => {
  if (!axios || typeof axios.interceptors?.request?.use !== 'function') return;
  if (axios[FLAG]) return;
  axios[FLAG] = true;

  axios.interceptors.request.use((config) => {
    // Build-time gate: CRA's DefinePlugin replaces this with the literal
    // string at build time, so any production bundle has the comparison
    // compiled to ``'production' !== 'production'`` (false) and the rest
    // of this function is dead-code-eliminated. Catches localhost,
    // ngrok-tunneled dev servers, and any other non-prod host without
    // requiring a hostname allow-list. The Node-side swa-proxy Function
    // also has NODE_ENV=production set in SWA app settings, so the
    // interceptor is inert there too.
    if (process.env.NODE_ENV === 'production') return config;

    const devUser = process.env.REACT_APP_DEV_USER;
    if (!devUser) return config;

    config.headers = config.headers || {};
    // Don't stomp an explicit header set by the caller (tests drive the
    // header directly to exercise specific server-side branches).
    if (!config.headers['X-Dev-Caller-Email']) {
      config.headers['X-Dev-Caller-Email'] = devUser;
    }
    return config;
  });
};

export default attachDevCallerEmailInterceptor;
